// src/context/UploadContext.js
// Upload state and logic that persists across navigation (UserCaptures -> CaptureViewer).

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { doc, setDoc, serverTimestamp, onSnapshot, deleteField } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, storage } from '../firebase';
import {
  messageToStage,
  inferFailStageFromError,
  getFailedStageIndex,
  normalizeProgressMessage,
  mapRedisStageToFrontend,
  loadingTitleForPipelinePercent,
  UPLOAD_INTERRUPTED_MESSAGE,
} from '../utils/uploadProgress';
import {
  UPLOAD_AND_PIPELINE_TIMEOUT_MS,
  BUTTON_REQUEST_TIMEOUT_MS,
  SET_CAPTURE_STATUS_FAILED_THROTTLE_MS,
  RUNPOD_STUCK_REDISPATCH_MS,
  UPLOAD_BYTE_STALL_DETECT_MS,
} from '../constants/timeouts';
import { getBackendBaseUrl, isDispatchedGpuMode } from '../utils/backendUrl';
import { assertCapturesStorageHeadroom } from '../utils/capturesStorageGate';
import { captureStoragePrefix, normalizedProjectId } from '../constants/studioProjects';
import { readVideoFileSourceMetadata } from '../utils/readVideoFileDuration';
import { extractVideoMetadata } from '../utils/extractVideoMetadata';
import { createPipelineTimeline, logPipelineDiagnostic } from '../utils/pipelineDiagnostics';

const UploadContext = createContext(null);

const IS_DISPATCHED_MODE = isDispatchedGpuMode();

const UPLOAD_PROGRESS_THROTTLE_MS = 100;

function getPipelineStageTitle(message, stage, sfmPhase, final, pipelinePercent) {
  const m = (message || '').toLowerCase();
  if (final === 'viewer-ready') return 'RECONSTRUCTION COMPLETE';
  if (final === 'gaussian-complete') return 'GAUSSIAN TRAINING';
  const pct = typeof pipelinePercent === 'number' ? pipelinePercent : 0;
  const fromPercent = loadingTitleForPipelinePercent(pct, message);
  if (fromPercent !== 'PROCESSING') return fromPercent;
  if (m.includes('gaussian') || stage === 4) return 'GAUSSIAN TRAINING';
  if (m.includes('sfm') || m.includes('structure from motion') || stage === 3) return 'STRUCTURE FROM MOTION';
  if (m.includes('frame') || sfmPhase === 'conducting' || stage >= 2) return 'FRAME EXTRACTION';
  if (m.includes('reconnecting')) return 'RECONNECTING';
  if (stage < 0) return 'QUEUEING';
  if (m.includes('in queue') || m === 'queued') return 'QUEUEING';
  if (m.includes('setting up pipeline') || m.includes('setting up reconstruction')) return 'QUEUEING';
  return 'PROCESSING';
}

function isWorkerQueueingSignal(message, runpodJobStatus, pipelinePercent, workerStarted) {
  const rp = (runpodJobStatus || '').toLowerCase();
  if (rp === 'in_queue') return true;
  const m = (message || '').toLowerCase();
  if (m.includes('setting up pipeline') || m.includes('setting up reconstruction')) return true;
  if (!workerStarted && (pipelinePercent ?? 0) === 0) return true;
  if (m.includes('in queue') || m === 'queued') return true;
  return false;
}

function deriveVideoNameFromCaptureId(captureId) {
  if (!captureId || typeof captureId !== 'string') return 'video';
  const idx = captureId.lastIndexOf('-');
  if (idx <= 0) return captureId;
  return captureId.slice(0, idx);
}

const UPLOAD_SESSION_KEY = 'volugraph_upload_state';

function saveUploadSession(state) {
  try { sessionStorage.setItem(UPLOAD_SESSION_KEY, JSON.stringify(state)); } catch {}
}
function loadUploadSession() {
  try {
    const raw = sessionStorage.getItem(UPLOAD_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const STUDIO_COLLECTION = 'studio';
const CAPTURES_SUB = 'captures';
function clearUploadSession() {
  try { sessionStorage.removeItem(UPLOAD_SESSION_KEY); } catch {}
}

export function UploadProvider({ children }) {
  const restored = useRef(loadUploadSession()).current;
  const [activeCaptureId, setActiveCaptureId] = useState(restored?.activeCaptureId ?? null);
  const [activeProjectId, setActiveProjectId] = useState(restored?.activeProjectId ?? null);
  const [activeShopifyProductId, setActiveShopifyProductId] = useState(restored?.activeShopifyProductId ?? null);
  const [isUploading, setIsUploading] = useState(restored?.isUploading ?? false);
  const [uploadPhase, setUploadPhase] = useState(restored?.uploadPhase ?? 'idle');
  const [uploadPercent, setUploadPercent] = useState(restored?.uploadPercent ?? 0);
  const [pipelinePercent, setPipelinePercent] = useState(restored?.pipelinePercent ?? 0);
  const [uploadBytesTransferred, setUploadBytesTransferred] = useState(restored?.uploadBytesTransferred ?? 0);
  const [uploadTotalBytes, setUploadTotalBytes] = useState(restored?.uploadTotalBytes ?? 0);
  const [queuePosition, setQueuePosition] = useState(restored?.queuePosition ?? null);
  const [progressPercent, setProgressPercent] = useState(restored?.progressPercent ?? 0);
  const [progressMessage, setProgressMessage] = useState(restored?.progressMessage ?? 'Preparing upload');
  const [currentStage, setCurrentStage] = useState(restored?.currentStage ?? 0);
  const [sfmConductingPhase, setSfmConductingPhase] = useState(null);
  const [finalPhase, setFinalPhase] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [framesExtracted, setFramesExtracted] = useState(null);
  const sfmTimeoutRef = useRef(null);
  const finalTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const uploadTaskRef = useRef(null);
  const uploadProgressThrottleRef = useRef(null);
  const uploadProgressSnapshotRef = useRef({ transferred: 0, total: 0 });
  const uploadPhaseRef = useRef('idle');
  const workerProgressStartedRef = useRef(false);
  const runpodJobStatusRef = useRef(null);
  const activeCaptureIdRef = useRef(null);
  const currentStageRef = useRef(0);
  const lastBackendEventAtRef = useRef(0);
  const lastConfirmedPercentRef = useRef(0);
  const lastConfirmedEventAtRef = useRef(0);
  const pipelinePercentRef = useRef(0);
  const uploadPercentRef = useRef(0);
  const stageEnteredAtRef = useRef(0);
  const statusFailedThrottleRef = useRef({ captureId: null, at: 0 });
  const firestoreUnsubRef = useRef(null);
  const stuckCheckTimeoutRef = useRef(null);
  const uploadStallTimeoutRef = useRef(null);
  const lastUploadByteProgressAtRef = useRef(0);
  const restoredSessionRef = useRef(restored);
  const activeProjectIdRef = useRef(null);
  const activeStudioUidRef = useRef(restored?.activeStudioUid ?? null);
  const pipelineTimelineRef = useRef(null);
  const prevUploadPhaseRef = useRef('idle');

  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const backendBaseUrl = getBackendBaseUrl();

  uploadPhaseRef.current = uploadPhase;
  pipelinePercentRef.current = pipelinePercent;
  uploadPercentRef.current = uploadPercent;

  const effectiveProgressPercent = uploadPhase === 'uploading' ? uploadPercent : pipelinePercent;
  const stageTitle = uploadPhase === 'uploading'
    ? 'UPLOADING VIDEO'
    : (uploadPhase === 'queueing'
      ? (queuePosition != null ? `QUEUEING (${queuePosition} ahead)` : 'QUEUEING')
      : getPipelineStageTitle(progressMessage, currentStage, sfmConductingPhase, finalPhase, pipelinePercent));
  currentStageRef.current = currentStage;

  useEffect(() => {
    if (uploadPhase !== 'uploading') return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploadPhase]);

  useEffect(() => {
    activeProjectIdRef.current = activeProjectId;
  }, [activeProjectId]);

  useEffect(() => {
    setProgressPercent(effectiveProgressPercent);
  }, [effectiveProgressPercent]);

  useEffect(() => {
    const prev = prevUploadPhaseRef.current;
    if (prev === 'queueing' && uploadPhase === 'pipeline') {
      logPipelineDiagnostic('ui_queueing_to_pipeline', {
        captureId: activeCaptureId,
        projectId: activeProjectId,
      });
      pipelineTimelineRef.current?.mark('ui_queueing_to_pipeline');
    }
    prevUploadPhaseRef.current = uploadPhase;
  }, [uploadPhase, activeCaptureId, activeProjectId]);

  useEffect(() => {
    if (activeCaptureId && (isUploading || effectiveProgressPercent > 0 || uploadPhase !== 'idle')) {
      saveUploadSession({
        activeCaptureId,
        activeProjectId,
        activeShopifyProductId,
        activeStudioUid: activeStudioUidRef.current,
        isUploading,
        uploadPhase,
        uploadPercent,
        pipelinePercent,
        uploadBytesTransferred,
        uploadTotalBytes,
        queuePosition,
        progressPercent: effectiveProgressPercent,
        progressMessage,
        currentStage,
      });
    } else if (!activeCaptureId) {
      clearUploadSession();
    }
  }, [
    activeCaptureId,
    activeProjectId,
    activeShopifyProductId,
    isUploading,
    uploadPhase,
    uploadPercent,
    pipelinePercent,
    uploadBytesTransferred,
    uploadTotalBytes,
    queuePosition,
    effectiveProgressPercent,
    progressMessage,
    currentStage,
  ]);

  /** No-op: previously cleared synthetic progress tick (removed; ring shows server percent only). */
  const clearHeartbeat = useCallback(() => {}, []);

  const cleanupFirestoreListener = useCallback(() => {
    if (firestoreUnsubRef.current) {
      firestoreUnsubRef.current();
      firestoreUnsubRef.current = null;
    }
  }, []);

  /* ------------------------------------------------------------------ */
  /*  cancelUpload                                                       */
  /* ------------------------------------------------------------------ */

  const cancelUpload = useCallback(async (captureIdOverride) => {
    if (stuckCheckTimeoutRef.current) {
      clearTimeout(stuckCheckTimeoutRef.current);
      stuckCheckTimeoutRef.current = null;
    }
    if (uploadTaskRef.current) {
      try { uploadTaskRef.current.cancel(); } catch (_) { /* ignore */ }
      uploadTaskRef.current = null;
    }
    if (uploadProgressThrottleRef.current) {
      clearTimeout(uploadProgressThrottleRef.current);
      uploadProgressThrottleRef.current = null;
    }
    const cid = captureIdOverride ?? activeCaptureIdRef.current ?? activeCaptureId;
    let cancelResult = { ok: true, terminated: false, message: 'No active pipeline' };
    if (cid) {
      try {
        if (IS_DISPATCHED_MODE) {
          const cancelCaptureFn = httpsCallable(functions, 'cancelCapture');
          const res = await cancelCaptureFn({
            captureId: cid,
            projectId: activeProjectIdRef.current ?? undefined,
            studioUid: activeStudioUidRef.current ?? undefined,
          });
          cancelResult = res.data;
        } else {
          const token = await auth.currentUser?.getIdToken?.();
          const cancelController = new AbortController();
          const cancelTimeoutId = setTimeout(() => cancelController.abort(), BUTTON_REQUEST_TIMEOUT_MS);
          const res = await fetch(`${backendBaseUrl}/cancel`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ captureId: cid }),
            signal: cancelController.signal,
          });
          clearTimeout(cancelTimeoutId);
          const payload = await res.json().catch(() => ({}));
          if (!res.ok) {
            cancelResult = {
              ok: false,
              terminated: false,
              message: payload?.error || `Cancel request failed (${res.status})`,
            };
          } else {
            cancelResult = {
              ok: true,
              terminated: !!payload?.terminated,
              message: payload?.message || 'Cancel requested',
            };
          }
        }
      } catch (e) {
        cancelResult = {
          ok: false,
          terminated: false,
          message: (e?.name === 'AbortError' ? 'Request timed out' : null) || e?.message || 'Cancel request failed',
        };
      }
    }
    if (abortControllerRef.current) abortControllerRef.current.abort();
    clearHeartbeat();
    cleanupFirestoreListener();
    setIsUploading(false);
    setUploadError(null);
    setUploadPhase('idle');
    workerProgressStartedRef.current = false;
    return cancelResult;
  }, [activeCaptureId, backendBaseUrl, clearHeartbeat, cleanupFirestoreListener]);

  activeCaptureIdRef.current = activeCaptureId;

  const clearUpload = useCallback(() => {
    if (uploadTaskRef.current) {
      try { uploadTaskRef.current.cancel(); } catch (_) { /* ignore */ }
      uploadTaskRef.current = null;
    }
    if (uploadProgressThrottleRef.current) {
      clearTimeout(uploadProgressThrottleRef.current);
      uploadProgressThrottleRef.current = null;
    }
    const now = Date.now();
    clearHeartbeat();
    cleanupFirestoreListener();
    clearUploadSession();
    setActiveCaptureId(null);
    setActiveProjectId(null);
    setActiveShopifyProductId(null);
    setIsUploading(false);
    setUploadPhase('idle');
    setUploadPercent(0);
    setPipelinePercent(0);
    setUploadBytesTransferred(0);
    setUploadTotalBytes(0);
    setQueuePosition(null);
    setProgressPercent(0);
    setProgressMessage('Preparing upload');
    setCurrentStage(0);
    setSfmConductingPhase(null);
    setFinalPhase(null);
    setUploadError(null);
    setFramesExtracted(null);
    workerProgressStartedRef.current = false;
    runpodJobStatusRef.current = null;
    lastBackendEventAtRef.current = now;
    lastConfirmedEventAtRef.current = now;
    lastConfirmedPercentRef.current = 0;
    stageEnteredAtRef.current = now;
  }, [clearHeartbeat, cleanupFirestoreListener]);

  useEffect(() => {
    return () => {
      clearHeartbeat();
      cleanupFirestoreListener();
      if (uploadStallTimeoutRef.current) {
        clearTimeout(uploadStallTimeoutRef.current);
        uploadStallTimeoutRef.current = null;
      }
    };
  }, [clearHeartbeat, cleanupFirestoreListener]);

  const clearUploadStallWatch = useCallback(() => {
    if (uploadStallTimeoutRef.current) {
      clearTimeout(uploadStallTimeoutRef.current);
      uploadStallTimeoutRef.current = null;
    }
  }, []);

  const markUploadInterrupted = useCallback(async (message = UPLOAD_INTERRUPTED_MESSAGE) => {
    const cid = activeCaptureIdRef.current;
    const pid = activeProjectIdRef.current;
    const uid = activeStudioUidRef.current || auth.currentUser?.uid || null;
    if (uploadTaskRef.current) {
      try { uploadTaskRef.current.cancel(); } catch (_) { /* ignore */ }
      uploadTaskRef.current = null;
    }
    clearUploadStallWatch();
    cleanupFirestoreListener();
    clearHeartbeat();
    setUploadError(message);
    setUploadPhase('failed');
    setProgressMessage('Error');
    setIsUploading(false);
    if (cid && uid && pid) {
      const captureRef = doc(db, 'users', uid, STUDIO_COLLECTION, pid, CAPTURES_SUB, cid);
      const failedPercent = Math.round(
        Math.max(
          0,
          Number.isFinite(uploadPercentRef.current) ? uploadPercentRef.current : 0,
        ),
      );
      try {
        await setDoc(captureRef, {
          updatedAt: serverTimestamp(),
          'processing.status': 'failed',
          'processing.failedAt': 'Upload',
          'processing.failedPercent': failedPercent,
          'processing.failedStageIndex': 0,
        }, { merge: true });
      } catch (e) {
        console.error('Failed to mark interrupted upload as failed', e);
      }
    }
    setActiveCaptureId(null);
    setActiveProjectId(null);
    clearUploadSession();
  }, [clearHeartbeat, clearUploadStallWatch, cleanupFirestoreListener]);

  const scheduleUploadStallWatch = useCallback(() => {
    clearUploadStallWatch();
    uploadStallTimeoutRef.current = setTimeout(() => {
      uploadStallTimeoutRef.current = null;
      if (uploadPhaseRef.current !== 'uploading') return;
      if (!activeCaptureIdRef.current) return;
      const elapsed = Date.now() - (lastUploadByteProgressAtRef.current || 0);
      if (elapsed < UPLOAD_BYTE_STALL_DETECT_MS) {
        scheduleUploadStallWatch();
        return;
      }
      if (!uploadTaskRef.current) {
        void markUploadInterrupted();
        return;
      }
      if ((uploadPercentRef.current ?? 0) < 100) {
        void markUploadInterrupted();
      }
    }, UPLOAD_BYTE_STALL_DETECT_MS);
  }, [clearUploadStallWatch, markUploadInterrupted]);

  /** Session restored after reload: in-memory upload task is gone — fail fast instead of stuck 0%. */
  useEffect(() => {
    const session = restoredSessionRef.current;
    if (!session?.isUploading || !session?.activeCaptureId) return;
    if (session.uploadPhase !== 'uploading') return;
    if (uploadTaskRef.current) return;
    const run = () => {
      if (!auth.currentUser?.uid && !session.activeStudioUid) return;
      void markUploadInterrupted();
    };
    if (auth.currentUser?.uid) {
      run();
      return undefined;
    }
    const unsub = auth.onAuthStateChanged((user) => {
      if (user?.uid) {
        unsub();
        run();
      }
    });
    return () => unsub();
  }, [markUploadInterrupted]);

  useEffect(() => {
    if (uploadPhase !== 'uploading' || !activeCaptureId) {
      clearUploadStallWatch();
      return undefined;
    }
    lastUploadByteProgressAtRef.current = Date.now();
    scheduleUploadStallWatch();
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (uploadPhaseRef.current !== 'uploading' || !activeCaptureIdRef.current) return;
      const elapsed = Date.now() - (lastUploadByteProgressAtRef.current || 0);
      if (elapsed >= UPLOAD_BYTE_STALL_DETECT_MS) {
        if (!uploadTaskRef.current || (uploadPercentRef.current ?? 0) < 100) {
          void markUploadInterrupted();
        }
      } else {
        scheduleUploadStallWatch();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearUploadStallWatch();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [uploadPhase, activeCaptureId, clearUploadStallWatch, scheduleUploadStallWatch, markUploadInterrupted]);

  /* ------------------------------------------------------------------ */
  /*  startUpload                                                        */
  /* ------------------------------------------------------------------ */

  const startUpload = useCallback(
    async (file, captureId, userId, projectId, sourceMediaHints = null, shopifyProductId = null) => {
      if (!captureId || !file) return;

      const pid = normalizedProjectId(projectId);
      pipelineTimelineRef.current = createPipelineTimeline(captureId);
      workerProgressStartedRef.current = false;
      runpodJobStatusRef.current = null;
      logPipelineDiagnostic('upload_started', { captureId, projectId: pid, shopifyProductId });
      setActiveProjectId(pid);
      setActiveShopifyProductId(shopifyProductId ? String(shopifyProductId).trim() : null);
      activeStudioUidRef.current = userId || null;

      if (userId && auth.currentUser?.uid === userId) {
        try {
          await auth.currentUser.getIdToken();
        } catch (_) {
          /* Storage/Firestore may still proceed */
        }
      }

      const captureRef = userId
        ? doc(db, 'users', userId, STUDIO_COLLECTION, pid, CAPTURES_SUB, captureId)
        : null;
      const setCaptureStatusFailed = async (failStage = null, failedPercentOverride = null) => {
        if (!captureRef) return;
        const now = Date.now();
        const prev = statusFailedThrottleRef.current;
        if (prev.captureId === captureId && now - prev.at < SET_CAPTURE_STATUS_FAILED_THROTTLE_MS) return;
        const localFailedPercent = Number.isFinite(failedPercentOverride)
          ? Math.round(Math.max(0, failedPercentOverride))
          : Math.round(
            Math.max(
              0,
              Number.isFinite(lastConfirmedPercentRef.current) ? lastConfirmedPercentRef.current : 0,
              Number.isFinite(pipelinePercentRef.current) ? pipelinePercentRef.current : 0,
              Number.isFinite(uploadPercentRef.current) ? uploadPercentRef.current : 0,
            ),
          );
        const failedStageIndex = failStage ? getFailedStageIndex(failStage) : -1;
        try {
          await setDoc(captureRef, {
            updatedAt: serverTimestamp(),
            'processing.status': 'failed',
            'processing.failedAt': failStage,
            'processing.failedPercent': localFailedPercent,
            ...(failedStageIndex >= 0 ? { 'processing.failedStageIndex': failedStageIndex } : {}),
          }, { merge: true });
          statusFailedThrottleRef.current = { captureId, at: Date.now() };
        } catch (e) {
          console.error('Failed to update capture status to failed', e);
        }
      };

      workerProgressStartedRef.current = false;
      runpodJobStatusRef.current = null;
      activeCaptureIdRef.current = captureId;
      setActiveCaptureId(captureId);
      setIsUploading(true);
      setUploadPhase('uploading');
      setUploadPercent(0);
      setPipelinePercent(0);
      setUploadBytesTransferred(0);
      setUploadTotalBytes(typeof file.size === 'number' ? file.size : 0);
      setQueuePosition(null);
      setProgressPercent(0);
      setProgressMessage('UPLOADING VIDEO');
      setUploadError(null);
      setCurrentStage(1);
      setSfmConductingPhase(null);
      setFinalPhase(null);
      if (sfmTimeoutRef.current) clearTimeout(sfmTimeoutRef.current);
      if (finalTimeoutRef.current) clearTimeout(finalTimeoutRef.current);
      clearHeartbeat();
      cleanupFirestoreListener();
      const now = Date.now();
      lastBackendEventAtRef.current = now;
      lastConfirmedEventAtRef.current = now;
      lastConfirmedPercentRef.current = 0;
      stageEnteredAtRef.current = now;

      let hasError = false;
      let pipelineAccepted = false;

      /* ---------- upload video to Firebase Storage ---------- */
      let token = null;
      try {
        token = await auth.currentUser?.getIdToken?.();
      } catch (_) {}
      const videoName = deriveVideoNameFromCaptureId(captureId);
      const uploadPath = `${captureStoragePrefix(userId, pid, captureId)}/uploads/${videoName}.mp4`;
      try {
        await assertCapturesStorageHeadroom(functions, file.size);
      } catch (e) {
        hasError = true;
        clearHeartbeat();
        cleanupFirestoreListener();
        setUploadError(e?.message || 'Storage check failed');
        setProgressMessage('Error');
        await setCaptureStatusFailed('Upload', uploadPercentRef.current);
        setIsUploading(false);
        setActiveCaptureId(null);
        setActiveProjectId(null);
        clearUploadSession();
        return;
      }
      const contentType = file?.type || 'video/mp4';
      const emitUploadByteProgress = (transferred, total) => {
        uploadProgressSnapshotRef.current = { transferred, total };
        const safeTotal = total > 0 ? total : (typeof file.size === 'number' ? file.size : 0);
        const pct = safeTotal > 0 ? Math.min(100, Math.round((transferred / safeTotal) * 100)) : 0;
        lastUploadByteProgressAtRef.current = Date.now();
        setUploadBytesTransferred(transferred);
        setUploadTotalBytes(safeTotal);
        setUploadPercent(pct);
        scheduleUploadStallWatch();
      };
      const scheduleUploadByteProgress = (transferred, total) => {
        uploadProgressSnapshotRef.current = { transferred, total };
        const now = Date.now();
        const elapsed = now - (scheduleUploadByteProgress.lastEmitAt || 0);
        if (elapsed >= UPLOAD_PROGRESS_THROTTLE_MS) {
          scheduleUploadByteProgress.lastEmitAt = now;
          emitUploadByteProgress(transferred, total);
          return;
        }
        if (uploadProgressThrottleRef.current) return;
        uploadProgressThrottleRef.current = setTimeout(() => {
          uploadProgressThrottleRef.current = null;
          scheduleUploadByteProgress.lastEmitAt = Date.now();
          const snap = uploadProgressSnapshotRef.current;
          emitUploadByteProgress(snap.transferred, snap.total);
        }, UPLOAD_PROGRESS_THROTTLE_MS - elapsed);
      };
      scheduleUploadByteProgress.lastEmitAt = 0;

      const uploadTask = uploadBytesResumable(storageRef(storage, uploadPath), file, { contentType });
      uploadTaskRef.current = uploadTask;
      try {
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              scheduleUploadByteProgress(snapshot.bytesTransferred, snapshot.totalBytes);
            },
            reject,
            resolve,
          );
        });
      } catch (e) {
        if (uploadTaskRef.current === uploadTask) uploadTaskRef.current = null;
        if (e?.code === 'storage/canceled') {
          hasError = true;
          clearHeartbeat();
          cleanupFirestoreListener();
          setUploadError('Cancelled by user');
          setProgressMessage('Cancelled');
          setUploadPhase('failed');
          setIsUploading(false);
          setActiveCaptureId(null);
          setActiveProjectId(null);
          clearUploadSession();
          return;
        }
        hasError = true;
        clearHeartbeat();
        cleanupFirestoreListener();
        setUploadError(e?.message || 'Upload failed');
        setProgressMessage('Error');
        await setCaptureStatusFailed('Upload', uploadPercentRef.current);
        setUploadPhase('failed');
        setIsUploading(false);
        setActiveCaptureId(null);
        setActiveProjectId(null);
        clearUploadSession();
        return;
      } finally {
        if (uploadProgressThrottleRef.current) {
          clearTimeout(uploadProgressThrottleRef.current);
          uploadProgressThrottleRef.current = null;
        }
        if (uploadTaskRef.current === uploadTask) uploadTaskRef.current = null;
      }
      emitUploadByteProgress(
        uploadProgressSnapshotRef.current.total || file.size || 0,
        uploadProgressSnapshotRef.current.total || file.size || 0,
      );
      setUploadPhase('queueing');
      setPipelinePercent(0);
      lastConfirmedPercentRef.current = 0;
      setProgressMessage('QUEUEING');
      setCurrentStage(0);
      pipelineTimelineRef.current?.mark('upload_complete', {
        bytes: file.size,
        shopifyProductId,
      });
      try {
        let hints = sourceMediaHints;
        if (!hints && captureRef) {
          const meta = await readVideoFileSourceMetadata(file);
          hints = {
            sourceVideoSizeBytes: typeof file.size === 'number' ? file.size : null,
            sourceVideoResolutionFps: meta.sourceVideoResolutionFps,
            sourceVideoDurationSeconds:
              meta.durationSeconds != null && Number.isFinite(meta.durationSeconds)
                ? Math.round(meta.durationSeconds * 10) / 10
                : null,
          };
        }
        let videoMeta = null;
        if (captureRef) {
          try {
            videoMeta = await extractVideoMetadata(file);
          } catch (_) {
            videoMeta = null;
          }
        }
        if (captureRef && (hints || videoMeta)) {
          const patch = { updatedAt: serverTimestamp() };
          if (hints) {
            if (hints.sourceVideoSizeBytes != null) {
              patch['processing.sourceVideoSizeBytes'] = hints.sourceVideoSizeBytes;
            }
            if (hints.sourceVideoResolutionFps != null) {
              patch['processing.sourceVideoResolutionFps'] = hints.sourceVideoResolutionFps;
            }
            if (hints.sourceVideoDurationSeconds != null) {
              patch['processing.sourceVideoDurationSeconds'] = hints.sourceVideoDurationSeconds;
              patch.sourceVideoDurationSeconds = hints.sourceVideoDurationSeconds;
            }
            if (hints.sourceProjection != null) {
              patch['processing.sourceProjection'] = hints.sourceProjection;
            }
          }
          if (videoMeta) {
            patch.videoMeta = {
              createdAt: videoMeta.createdAt,
              lat: videoMeta.lat,
              lng: videoMeta.lng,
              altitude: videoMeta.altitude,
              cameraModel: videoMeta.cameraModel,
              focalLength: videoMeta.focalLength,
            };
          }
          await setDoc(captureRef, patch, { merge: true });
        }
        if (captureRef) {
          await setDoc(captureRef, {
            updatedAt: serverTimestamp(),
            'processing.status': 'queued',
            'processing.failedAt': deleteField(),
            'processing.failStatus': deleteField(),
            'processing.failedStageIndex': deleteField(),
            'processing.failedPercent': deleteField(),
          }, { merge: true });
        }
      } catch (e) {
        console.error('Failed to merge source video metadata after upload', e);
      }

      /* ====================================================== */
      /*  DISPATCHED MODE (production – Cloud Functions)         */
      /* ====================================================== */
      if (IS_DISPATCHED_MODE) {
        const onComplete = () => {
          if (stuckCheckTimeoutRef.current) {
            clearTimeout(stuckCheckTimeoutRef.current);
            stuckCheckTimeoutRef.current = null;
          }
          cleanupFirestoreListener();
          clearHeartbeat();
          clearUploadSession();
          setIsUploading(false);
          setUploadError(null);
          setActiveCaptureId(null);
          setActiveProjectId(null);
        };

        const onFail = async (msg, failStage, failedPercent = null) => {
          hasError = true;
          if (stuckCheckTimeoutRef.current) {
            clearTimeout(stuckCheckTimeoutRef.current);
            stuckCheckTimeoutRef.current = null;
          }
          cleanupFirestoreListener();
          clearHeartbeat();
          setUploadPhase('failed');
          setUploadError(msg || 'Something went wrong');
          setProgressMessage(msg || 'Error');
          if (failStage) await setCaptureStatusFailed(failStage, failedPercent);
        };

        // Subscribe to Firestore capture doc for real-time progress
        if (captureRef) {
          firestoreUnsubRef.current = onSnapshot(captureRef, (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();
            const status = data?.processing?.status;
            const prog = data?.progress;
            const frames = data?.processing?.frames;

            if (status === 'completed') {
              if (stuckCheckTimeoutRef.current) {
                clearTimeout(stuckCheckTimeoutRef.current);
                stuckCheckTimeoutRef.current = null;
              }
              if (finalTimeoutRef.current) clearTimeout(finalTimeoutRef.current);
              setUploadPhase('done');
              setFinalPhase('gaussian-complete');
              setPipelinePercent(100);
              setProgressMessage('RECONSTRUCTION COMPLETE');
              setCurrentStage(6);
              finalTimeoutRef.current = setTimeout(() => {
                setFinalPhase('viewer-ready');
                onComplete();
              }, 1500);
              return;
            }

            if (status === 'failed') {
              if (stuckCheckTimeoutRef.current) {
                clearTimeout(stuckCheckTimeoutRef.current);
                stuckCheckTimeoutRef.current = null;
              }
              const failedAt = data?.processing?.failedAt;
              const isCancelled = failedAt === 'Cancelled' || failedAt === 'Dispatch';
              if (!isCancelled) {
                const failedPercent = Number.isFinite(data?.processing?.failedPercent)
                  ? Number(data.processing.failedPercent)
                  : (Number.isFinite(data?.progress?.percent) ? Number(data.progress.percent) : null);
                onFail(
                  failedAt ? `Error: ${failedAt}` : 'Pipeline failed',
                  failedAt || 'Unknown',
                  failedPercent,
                );
              } else {
                hasError = true;
                cleanupFirestoreListener();
                clearHeartbeat();
                setUploadError('Cancelled by user');
                setProgressMessage('Cancelled');
              }
              return;
            }

            const hasProgressSignal =
              Number.isFinite(Number(prog?.percent)) && Number(prog.percent) > 0;
            const hasStageSignal = typeof prog?.message === 'string'
              && messageToStage(prog.message) >= 2;
            if (status === 'queued' && !hasProgressSignal && !hasStageSignal) {
              setUploadPhase('queueing');
              setPipelinePercent(0);
              lastConfirmedPercentRef.current = 0;
              setProgressMessage('QUEUEING');
              setCurrentStage(-1);
              return;
            }

            if (frames != null && Number.isFinite(Number(frames))) {
              setFramesExtracted(Number(frames));
            }

            if (prog) {
              const eventNow = Date.now();
              lastBackendEventAtRef.current = eventNow;
              lastConfirmedEventAtRef.current = eventNow;

              if (prog.queuePosition != null) {
                setQueuePosition(Number(prog.queuePosition));
                setUploadPhase('queueing');
                setPipelinePercent(0);
                lastConfirmedPercentRef.current = 0;
                setProgressMessage('QUEUEING');
                setCurrentStage(-1);
                return;
              }
              setQueuePosition(null);

              const pct = Number.isFinite(prog.percent) ? prog.percent : 0;
              const msgLower = (prog.message || '').toLowerCase();
              const queueSignal = isWorkerQueueingSignal(
                prog.message,
                runpodJobStatusRef.current,
                pct,
                workerProgressStartedRef.current,
              );
              if (queueSignal && pct === 0) {
                setUploadPhase('queueing');
                setPipelinePercent(0);
                setProgressMessage('QUEUEING');
                setCurrentStage(-1);
                return;
              }
              if (pct > 0 && !workerProgressStartedRef.current) {
                workerProgressStartedRef.current = true;
                lastConfirmedPercentRef.current = 0;
                setUploadPhase('pipeline');
                pipelineTimelineRef.current?.mark('first_worker_progress', {
                  pct,
                  source: 'firestore_listener',
                  message: prog.message || null,
                });
              } else if (!workerProgressStartedRef.current && msgLower.includes('extracting frames')) {
                workerProgressStartedRef.current = true;
                lastConfirmedPercentRef.current = 0;
                setUploadPhase('pipeline');
                pipelineTimelineRef.current?.mark('first_worker_progress', {
                  pct,
                  source: 'firestore_extracting_frames',
                });
              }
              const confirmedPercent = Math.max(lastConfirmedPercentRef.current, pct);
              lastConfirmedPercentRef.current = confirmedPercent;
              setPipelinePercent((prev) => Math.max(prev, confirmedPercent));

              if (pct > 3 && stuckCheckTimeoutRef.current) {
                clearTimeout(stuckCheckTimeoutRef.current);
                stuckCheckTimeoutRef.current = null;
              }

              if (prog.message) {
                const normalized = normalizeProgressMessage(prog.message);
                setProgressMessage(normalized);
                setCurrentStage((prev) => {
                  const next = Math.max(prev, messageToStage(prog.message));
                  if (next > prev) stageEnteredAtRef.current = Date.now();
                  return next;
                });

                const m = (prog.message || '').toLowerCase();
                if (m.includes('initiating') && m.includes('sfm')) {
                  if (sfmTimeoutRef.current) clearTimeout(sfmTimeoutRef.current);
                  setSfmConductingPhase('initiating');
                  sfmTimeoutRef.current = setTimeout(() => setSfmConductingPhase('conducting'), 2000);
                } else if (
                  m.includes('done converting video to images') ||
                  m.includes('extracting superpoint features') ||
                  m.includes('matching features with lightglue') ||
                  m.includes('running bundle adjustment') ||
                  (m.includes('sfm') && m.includes('complete'))
                ) {
                  setSfmConductingPhase('conducting');
                }
              }
            }
          }, (err) => {
            const code = err?.code || '';
            const msg = (err?.message || '').toLowerCase();
            if (code === 'unavailable' || msg.includes('offline') || msg.includes('failed to get document')) {
              return;
            }
            if (process.env.NODE_ENV === 'development') {
              // eslint-disable-next-line no-console
              console.warn('[UploadContext] Firestore capture listener', err?.code, err?.message);
            }
          });
        }

        // Dispatch via Cloud Function
        try {
          pipelineTimelineRef.current?.mark('dispatch_submit', { captureId, projectId: pid });
          logPipelineDiagnostic('dispatch_submit', { captureId, projectId: pid });
          const dispatchCaptureFn = httpsCallable(functions, 'dispatchCapture');
          const result = await dispatchCaptureFn({ captureId, projectId: pid, studioUid: userId });
          pipelineAccepted = true;
          pipelineTimelineRef.current?.mark('dispatch_result', {
            status: result.data?.status ?? null,
            jobId: result.data?.jobId ?? null,
            dispatchMode: result.data?.dispatchMode ?? null,
          });
          logPipelineDiagnostic('dispatch_result', {
            captureId,
            projectId: pid,
            status: result.data?.status ?? null,
            jobId: result.data?.jobId ?? null,
            dispatchMode: result.data?.dispatchMode ?? null,
          });

          if (result.data?.status === 'queued') {
            setUploadPhase('queueing');
            setPipelinePercent(0);
            lastConfirmedPercentRef.current = 0;
            setProgressMessage('QUEUEING');
            setCurrentStage(-1);
          }

          // Auto-check and re-dispatch if RunPod job timed out while worker was pulling image.
          if (stuckCheckTimeoutRef.current) clearTimeout(stuckCheckTimeoutRef.current);
          stuckCheckTimeoutRef.current = setTimeout(async () => {
            stuckCheckTimeoutRef.current = null;
            const cid = activeCaptureIdRef.current;
            if (!cid || hasError) return;
            try {
              const checkFn = httpsCallable(functions, 'checkRunPodJobAndRedispatch');
              const res = await checkFn({
                captureId: cid,
                projectId: activeProjectIdRef.current ?? pid,
                studioUid: activeStudioUidRef.current ?? userId,
              });
              if (res.data?.redispatch) {
                setProgressMessage('Reconnecting to worker…');
              }
            } catch (e) {
              // Silently ignore – user can retry manually
            }
          }, RUNPOD_STUCK_REDISPATCH_MS);
        } catch (err) {
          if (!hasError) {
            const msg = err?.message || 'Failed to start pipeline';
            await onFail(msg, inferFailStageFromError(msg) || 'Dispatch');
          }
          return;
        }
        return;
      }

      /* ====================================================== */
      /*  LOCAL MODE (development – HTTP + Redis status polling) */
      /* ====================================================== */
      let pollIntervalId = null;
      const cleanupListeners = () => {
        if (pollIntervalId != null) {
          clearInterval(pollIntervalId);
          pollIntervalId = null;
        }
      };

      /** Drives the in-tab upload + local-dev pipeline bar; studio CaptureViewer also uses `usePipelinePolling` when this session is not the active upload. */
      const applyPipelineStatus = (data) => {
        if (!data?.found) return;
        const st = (data.status || '').toLowerCase();

        if (st === 'cancelled') {
          hasError = true;
          clearHeartbeat();
          setUploadError('Cancelled by user');
          setProgressMessage('Cancelled');
          if (pipelineAccepted) cleanupListeners();
          return;
        }

        if (st === 'failed') {
          hasError = true;
          clearHeartbeat();
          const msg = (data.failed_at != null && String(data.failed_at))
            ? `Error: ${data.failed_at}`
            : 'Pipeline failed';
          setUploadError(msg);
          setProgressMessage(msg);
          setUploadPhase('failed');
          if (workerProgressStartedRef.current) {
            const failStage = data.failed_at ?? inferFailStageFromError(msg) ?? 'Unknown';
            const failedPercent = Number.isFinite(data.failed_percent)
              ? Number(data.failed_percent)
              : (Number.isFinite(data.percent) ? Number(data.percent) : null);
            setCaptureStatusFailed(failStage, failedPercent);
          }
          if (pipelineAccepted) cleanupListeners();
          return;
        }

        if (st === 'completed') {
          const eventNow = Date.now();
          lastBackendEventAtRef.current = eventNow;
          lastConfirmedEventAtRef.current = eventNow;
          lastConfirmedPercentRef.current = 100;
          setUploadPhase('done');
          setPipelinePercent(100);
          setProgressMessage('RECONSTRUCTION COMPLETE');
          setCurrentStage(6);
          if (finalTimeoutRef.current) clearTimeout(finalTimeoutRef.current);
          setFinalPhase('gaussian-complete');
          finalTimeoutRef.current = setTimeout(() => {
            setFinalPhase('viewer-ready');
            if (pipelineAccepted) {
              cleanupListeners();
              clearHeartbeat();
              clearUploadSession();
              setIsUploading(false);
              setUploadError(null);
              setActiveCaptureId(null);
              setActiveProjectId(null);
            }
          }, 1500);
          return;
        }

        const rp = String(data.runpodJobStatus || '').trim().toLowerCase();
        runpodJobStatusRef.current = rp || null;
        if (st === 'queued' || data.queued || rp === 'in_queue') {
          lastBackendEventAtRef.current = Date.now();
          setUploadPhase('queueing');
          setPipelinePercent(0);
          lastConfirmedPercentRef.current = 0;
          setProgressMessage('QUEUEING');
          setCurrentStage(-1);
          return;
        }

        const pct = Number.isFinite(data.percent) ? data.percent : 0;
        const { stageTitle: redisStageTitle } = mapRedisStageToFrontend(data.stage, pct);
        const queueSignal = isWorkerQueueingSignal(
          redisStageTitle,
          rp,
          pct,
          workerProgressStartedRef.current,
        );
        if (queueSignal && pct === 0) {
          setUploadPhase('queueing');
          setPipelinePercent(0);
          setProgressMessage('QUEUEING');
          setCurrentStage(-1);
          return;
        }
        if (pct > 0 && !workerProgressStartedRef.current) {
          workerProgressStartedRef.current = true;
          lastConfirmedPercentRef.current = 0;
          setUploadPhase('pipeline');
          pipelineTimelineRef.current?.mark('first_worker_progress', {
            pct,
            source: 'redis_poll',
            stage: data.stage ?? null,
          });
        }
        const eventNow = Date.now();
        lastBackendEventAtRef.current = eventNow;
        lastConfirmedEventAtRef.current = eventNow;
        const confirmedPercent = Math.max(lastConfirmedPercentRef.current, pct);
        lastConfirmedPercentRef.current = confirmedPercent;
        setPipelinePercent((prev) => Math.max(prev, confirmedPercent));

        const msg = normalizeProgressMessage(redisStageTitle);
        setProgressMessage(msg);
        setCurrentStage((prev) => {
          const next = Math.max(prev, messageToStage(msg));
          if (next > prev) stageEnteredAtRef.current = Date.now();
          return next;
        });

        const m = msg.toLowerCase();
        const stTitle = redisStageTitle.toLowerCase();
        if (stTitle.includes('extracting frames')) {
          if (sfmTimeoutRef.current) clearTimeout(sfmTimeoutRef.current);
          setSfmConductingPhase('initiating');
          sfmTimeoutRef.current = setTimeout(() => setSfmConductingPhase('conducting'), 2000);
        } else if (stTitle.includes('structure') || stTitle.includes('sfm') || m.includes('structure from motion')) {
          setSfmConductingPhase('conducting');
        }
        if (stTitle.includes('extracting colmap') || m.includes('matching features') || m.includes('bundle adjustment')) {
          setSfmConductingPhase('conducting');
        }
        if (m.includes('sfm') && m.includes('complete')) {
          if (sfmTimeoutRef.current) clearTimeout(sfmTimeoutRef.current);
          setSfmConductingPhase(null);
        }
      };

      const pollOnce = async () => {
        const cid = activeCaptureIdRef.current;
        if (!cid || !backendBaseUrl) return;
        try {
          const url = `${backendBaseUrl.replace(/\/$/, '')}/pipeline/status/${encodeURIComponent(cid)}`;
          const res = await fetch(url);
          if (!res.ok) return;
          const data = await res.json();
          applyPipelineStatus(data);
        } catch (_) {}
      };

      pollIntervalId = setInterval(pollOnce, 2000);
      pollOnce();

      const controller = new AbortController();
      abortControllerRef.current = controller;
      const pipelineTimeoutId = setTimeout(() => controller.abort(), UPLOAD_AND_PIPELINE_TIMEOUT_MS);

      // Emit an immediate upload-intent signal so backend logs appear right away.
      try {
        await fetch(`${backendBaseUrl}/train/init`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ captureId, filename: file?.name || '' }),
        });
      } catch (_) {}

      try {
        const response = await fetch(`${backendBaseUrl}/train/from-storage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ captureId, projectId: pid }),
          signal: controller.signal,
        });
        clearTimeout(pipelineTimeoutId);

        const contentType = response.headers.get('content-type');
        let result;
        if (contentType && contentType.includes('application/json')) {
          result = await response.json();
        } else {
          await response.text();
          hasError = true;
          if (response.status === 404) {
            setUploadError(`Backend server not found at ${backendBaseUrl}/train/from-storage. Is the backend running?`);
          } else if (response.status >= 500) {
            setUploadError(`Backend server error (${response.status}). Check backend logs.`);
          } else {
            setUploadError(`Backend returned non-JSON response (${response.status}). Check backend configuration.`);
          }
          cleanupListeners();
          clearHeartbeat();
          return;
        }

        if (response.status === 202) {
          pipelineAccepted = true;
          return;
        }

        if (!response.ok) {
          hasError = true;
          clearHeartbeat();
          setUploadError(result.error || 'Upload failed');
          const failStage = result.failStage ?? inferFailStageFromError(result.error) ?? 'Unknown';
          await setCaptureStatusFailed(failStage);
        }
      } catch (err) {
        clearTimeout(pipelineTimeoutId);
        if (pipelineAccepted) {
          return;
        }
        if (err.name !== 'AbortError') {
          hasError = true;
          clearHeartbeat();
          if (err.message.includes('JSON') || err.message.includes('<!DOCTYPE')) {
            setUploadError(isLocalhost ? `Backend server error: Received HTML instead of JSON. Is the backend running at ${backendBaseUrl}?` : 'Servers offline');
          } else if (err.message === 'Failed to fetch') {
            setUploadError('Servers unavailable. Try again shortly.');
          } else {
            setUploadError(err.message || 'Upload failed');
          }
          const serversUnreachable = err.message === 'Failed to fetch' || err.message.includes('JSON') || err.message.includes('<!DOCTYPE');
          if (!serversUnreachable) {
            await setCaptureStatusFailed(inferFailStageFromError(err.message) || 'Unknown');
          }
        } else {
          if (err.name === 'AbortError') {
            hasError = true;
            clearHeartbeat();
            setUploadError('Request timed out');
          }
        }
        cleanupListeners();
      } finally {
        if (!pipelineAccepted) {
          cleanupListeners();
          clearHeartbeat();
          if (!hasError) {
            clearUploadSession();
            setIsUploading(false);
            setUploadError(null);
            setActiveCaptureId(null);
            setActiveProjectId(null);
          }
        }
      }
    },
    [backendBaseUrl, isLocalhost, clearHeartbeat, cleanupFirestoreListener, scheduleUploadStallWatch]
  );

  const setUploadFailed = useCallback((captureId, message) => {
    setActiveCaptureId(captureId);
    setIsUploading(false);
    setUploadError(message);
    setUploadPhase('failed');
    setUploadPercent(0);
    setPipelinePercent(0);
    setProgressPercent(0);
    setProgressMessage('Error');
    setCurrentStage(0);
    clearHeartbeat();
  }, [clearHeartbeat]);

  const value = {
    activeCaptureId,
    activeProjectId,
    activeShopifyProductId,
    isUploading,
    uploadPhase,
    uploadPercent,
    pipelinePercent,
    uploadBytesTransferred,
    uploadTotalBytes,
    queuePosition,
    progressPercent,
    progressMessage,
    currentStage,
    stageTitle,
    uploadError,
    framesExtracted,
    startUpload,
    cancelUpload,
    clearUpload,
    setUploadFailed,
  };

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}

export function useUpload() {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
}
